// Tag filter vocabulary — three axes exposed in the header filter
// popover. Each axis answers a distinct question about the card:
//
//   class     — "what kind of word?" (grammatical nature)
//   domain    — "what topic?" (broad semantic bucket)
//   register  — "what tone?" (formality / style)
//
// A card can carry any number of tags; only tags belonging to one of
// these axes surface as filter pills. Any other tag on the card stays
// visible on the card tile but is not filterable — this lets the free-
// form vocabulary grow without forcing us to revise the axis UI.
//
// Filter semantics: AND between axes, OR within an axis.
//
// Keep the axis vocabularies INTENTIONALLY BROAD. A 5000-card medical
// dataset still fits under `body` — specificity ("organ", "disease") is
// expressed as free tags, not as new axis entries. Review the bucketing
// 1-2x per year; splits should be rare.

export const CLASS_TAGS = [
  "verb",
  "noun",
  "adjective",
  "adverb",
  "phrase",
  "idiom",
  "question",
] as const;

export const DOMAIN_TAGS = [
  "body",          // anatomie, santé, médical, sensation
  "food",          // nourriture, boisson, cuisine, repas
  "nature",        // plantes, animaux, météo, géographie
  "society",       // famille, travail, éducation, politique, rôles
  "time",          // temps, durée, calendrier, fréquence
  "communication", // salutations, questions, dialogue, médias
  "mind",          // émotion, pensée, perception, caractère
  "action",        // mouvement, sport, gestes
  "object",        // outils, vêtements, mobilier, technologie
  "abstract",      // concepts, mesures, qualités, quantités
] as const;

export const REGISTER_TAGS = [
  "formal",
  "informal",
  "slang",
] as const;

export type ClassTag = (typeof CLASS_TAGS)[number];
export type DomainTag = (typeof DOMAIN_TAGS)[number];
export type RegisterTag = (typeof REGISTER_TAGS)[number];

/** The three filterable axes, in the order they appear in the popover
 *  (class first = most used, register last = least used). */
export const TAG_AXES = ["class", "domain", "register"] as const;
export type TagAxis = (typeof TAG_AXES)[number];

export const AXIS_TAGS: Record<TagAxis, readonly string[]> = {
  class: CLASS_TAGS,
  domain: DOMAIN_TAGS,
  register: REGISTER_TAGS,
};

/** Active filter state, one string[] per axis. Empty array on an axis
 *  means "no constraint on this axis". All-empty = no filter at all. */
export type TagFilter = Record<TagAxis, string[]>;

export function emptyTagFilter(): TagFilter {
  return { class: [], domain: [], register: [] };
}

/** Total number of active pills across every axis — drives the header
 *  button's count badge. */
export function tagFilterCount(f: TagFilter): number {
  return f.class.length + f.domain.length + f.register.length;
}

/** True when no axis carries any selection. */
export function isTagFilterEmpty(f: TagFilter): boolean {
  return tagFilterCount(f) === 0;
}

/** Inverse lookup: given a raw tag string, which axis does it belong to
 *  (if any)? Used when migrating legacy flat-array filter values, and
 *  could later back a "promote this free tag to an axis" UX. Returns
 *  null for tags that live outside every axis. */
export function axisForTag(tag: string): TagAxis | null {
  for (const axis of TAG_AXES) {
    if ((AXIS_TAGS[axis] as readonly string[]).includes(tag)) return axis;
  }
  return null;
}
